import { useState } from "react";
import styles from "./SupplierDetail.module.css";
import {
  useUpdateSupplier,
  useUpdateSupplierServices,
} from "../../../api/suppliers/useSuppliersApi";
import { useGetCountries } from "../../../api/geo/useCountries";
import SearchableSelect from "../../../components/BaseComponents/SearchableSelect/SearchableSelect";
import {
  X,
  Save,
  Info,
  Package,
  MapPin,
  Mail,
  Phone,
  User,
  Calendar,
  AlertCircle,
  Loader,
} from "lucide-react";

// ─── helpers ────────────────────────────────────────────────────────────────

const INITIAL_SERVICES = [
  { name: "Hotels",                   key: "Hotels" },
  { name: "Transfers",                key: "Transfers" },
  { name: "External Package Holiday", key: "External Package Holiday" },
  { name: "Incoming",                 key: "Incoming" },
  { name: "Car Rental",               key: "Car Rental" },
  { name: "Insurance",                key: "Insurance" },
  { name: "Visa",                     key: "Visa" },
  { name: "Flight Service",            key: "Flight Service" },
  { name: "Other Services",           key: "Other Services" },
];

/** Convert the services array from the backend into a { key: boolean } map */
const parseServices = (services) => {
  const map = {};
  if (Array.isArray(services)) {
    services.forEach((s) => {
      const key = s.serviceType ?? s.key;
      if (key) map[key] = !!s.enabled;
    });
  }
  return map;
};

const HOTEL_COMMERCIAL_ROLES = [
  { key: "MERCHANT", label: "Merchant (own risk)" },
  { key: "AGENT", label: "Agent (intermediary)" },
  { key: "BOTH", label: "Both" },
];

const OTHER_COMMERCIAL_ROLES = [
  { key: "MERCHANT", label: "Merchant (own risk)" },
  { key: "AGENT", label: "Agent (intermediary)" },
];

/** Parse the commercial roles from services array */
const parseServiceRoles = (services) => {
  const map = {};
  if (Array.isArray(services)) {
    services.forEach((s) => {
      const key = s.serviceType ?? s.key;
      if (key) map[key] = s.commercialRole || "MERCHANT";
    });
  }
  return map;
};

/** Parse defaultCommercialRole per service from services array */
const parseServiceDefaultRoles = (services) => {
  const map = {};
  if (Array.isArray(services)) {
    services.forEach((s) => {
      const key = s.serviceType ?? s.key;
      if (key && s.defaultCommercialRole) map[key] = s.defaultCommercialRole;
    });
  }
  return map;
};

/** Build the initial form state from the supplier prop */
const buildFormState = (supplier) => {
  const svcMap = parseServices(supplier.services);
  const roleMap = parseServiceRoles(supplier.services);
  const defaultRoleMap = parseServiceDefaultRoles(supplier.services);
  return {
    // overview
    code:          supplier.code          ?? "",
    name:          supplier.name          ?? "",
    type:          supplier.type          ?? "API",
    status:        supplier.status        ?? "Active",
    internalNotes: supplier.internalNotes ?? "",

    // services – each service key → boolean
    services: Object.fromEntries(
      INITIAL_SERVICES.map((s) => [s.key, svcMap[s.key] ?? false])
    ),

    // service commercial roles
    serviceRoles: Object.fromEntries(
      INITIAL_SERVICES.map((s) => [s.key, roleMap[s.key] ?? (s.key === "Hotels" ? "DEFAULT" : "MERCHANT")])
    ),

    // default role when commercialRole = BOTH (Hotels only)
    serviceDefaultRoles: Object.fromEntries(
      INITIAL_SERVICES.map((s) => [s.key, defaultRoleMap[s.key] ?? "MERCHANT"])
    ),

    // vat / country
    country: supplier.country ?? "France",
    vatRole: supplier.vatRole ?? "EU travel agency",

    // contact
    contactEmail:       supplier.contactEmail       ?? "",
    contactPhone:       supplier.contactPhone       ?? "",
    contactPersonName:  supplier.contactPersonName  ?? "",
  };
};

// ─── component ───────────────────────────────────────────────────────────────

const SupplierDetail = ({ supplier, onClose, onSaved }) => {
  const [activeTab, setActiveTab]   = useState("overview");
  const [form, setForm]             = useState(() => buildFormState(supplier));
  const [saveError, setSaveError]   = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const { execute: updateSupplier, loading: savingSupplier } = useUpdateSupplier();
  const { execute: updateServices, loading: savingServices } = useUpdateSupplierServices();
  const { data: countries } = useGetCountries({ limit: 300, active: true, sortBy: 'name', sortOrder: 'ASC' });
  const saving = savingSupplier || savingServices;

  // ── generic field setter ──────────────────────────────────────────────────
  const setField = (key, value) => {
    setSaveError(null);
    setSaveSuccess(false);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleService = (key) => {
    setSaveError(null);
    setSaveSuccess(false);
    setForm((prev) => ({
      ...prev,
      services: { ...prev.services, [key]: !prev.services[key] },
    }));
  };

  const setServiceRole = (key, role) => {
    setSaveError(null);
    setSaveSuccess(false);
    setForm((prev) => ({
      ...prev,
      serviceRoles: { ...prev.serviceRoles, [key]: role },
      serviceDefaultRoles: role !== "BOTH"
        ? { ...prev.serviceDefaultRoles, [key]: undefined }
        : prev.serviceDefaultRoles,
    }));
  };

  const setServiceDefaultRole = (key, defaultRole) => {
    setSaveError(null);
    setSaveSuccess(false);
    setForm((prev) => ({
      ...prev,
      serviceDefaultRoles: { ...prev.serviceDefaultRoles, [key]: defaultRole },
    }));
  };

  // ── validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!form.code.trim())  return "Supplier Code is required.";
    if (!form.name.trim())  return "Supplier Name is required.";
    if (!form.country)      return "Country is required.";
    if (!form.vatRole)      return "Supplier VAT Role is required.";
    if (!form.contactEmail.trim()) return "Contact Email is required.";
    const enabledServices = Object.values(form.services).some(Boolean);
    if (!enabledServices)   return "At least one service must be enabled.";
    return null;
  };

  // ── save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const err = validate();
    if (err) { setSaveError(err); return; }

    try {
      const supplierId = supplier.id;
      const payload = {
        code:             form.code,
        name:             form.name,
        type:             form.type,
        status:           form.status,
        internalNotes:    form.internalNotes,
        country:          form.country,
        vatRole:          form.vatRole,
        contactEmail:     form.contactEmail,
        contactPhone:     form.contactPhone,
        contactPersonName: form.contactPersonName,
      };

      const updated = await updateSupplier(supplierId, payload);

      const servicesArray = INITIAL_SERVICES.map((s) => {
        const role = form.serviceRoles[s.key] || "MERCHANT";
        return {
          serviceType: s.key,
          enabled: !!form.services[s.key],
          commercialRole: role,
          defaultCommercialRole: role === "BOTH" ? (form.serviceDefaultRoles[s.key] || "MERCHANT") : null,
        };
      });
      await updateServices(supplierId, servicesArray);

      setSaveSuccess(true);
      onSaved?.(updated);
    } catch (e) {
      setSaveError(e?.response?.data?.message ?? "Failed to save supplier.");
    }
  };

  // ── tabs config ───────────────────────────────────────────────────────────
  const tabs = [
    { id: "overview",  label: "Overview",              icon: Info    },
    { id: "services",  label: "Product / Service Scope", icon: Package },
    { id: "vat",       label: "VAT / Country",          icon: MapPin  },
    { id: "contact",   label: "Contact",                icon: Mail    },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />

      <div className={styles.drawer}>
        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>{supplier.name}</h2>
            <div className={styles.headerMeta}>
              <span
                className={`${styles.statusBadge} ${
                  styles[`status${form.status}`]
                }`}
              >
                {form.status}
              </span>
              <span className={styles.metaText}>ID: {supplier.supplierId || supplier.id}</span>
              <span className={styles.metaText}>Code: {form.code}</span>
            </div>
          </div>

          <div className={styles.headerActions}>
            <button className={styles.closeBtn} onClick={onClose} disabled={saving}>
              <X size={20} />
              Close
            </button>
            <button
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader size={18} className={styles.spinIcon} />
                  Saving…
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Inline feedback banner ── */}
        {(saveError || saveSuccess) && (
          <div
            className={
              saveError ? styles.bannerError : styles.bannerSuccess
            }
          >
            <AlertCircle size={16} />
            {saveError ?? "Supplier saved successfully."}
          </div>
        )}

        {/* ── Tabs Navigation ── */}
        <div className={styles.tabs}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`${styles.tab} ${
                  activeTab === tab.id ? styles.activeTab : ""
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* ── Content ── */}
        <div className={styles.content}>

          {/* ════ Overview Tab ════ */}
          {activeTab === "overview" && (
            <div className={styles.tabContent}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Basic Information</h3>
                <div className={styles.grid}>

                  {/* Supplier ID – read-only */}
                  <div className={styles.field}>
                    <label className={styles.label}>Supplier ID</label>
                    <input
                      type="text"
                      value={supplier.supplierId || supplier.id}
                      disabled
                      className={styles.inputDisabled}
                    />
                    <span className={styles.hint}>Read-only system identifier</span>
                  </div>

                  {/* Supplier Code */}
                  <div className={styles.field}>
                    <label className={styles.label}>
                      Supplier Code <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      value={form.code}
                      onChange={(e) => setField("code", e.target.value)}
                      className={styles.input}
                    />
                    <span className={styles.hint}>Required, must be unique</span>
                  </div>

                  {/* Supplier Name */}
                  <div className={styles.fieldFull}>
                    <label className={styles.label}>
                      Supplier Name <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      className={styles.input}
                    />
                  </div>

                  {/* Supplier Type */}
                  <div className={styles.field}>
                    <label className={styles.label}>
                      Supplier Type <span className={styles.required}>*</span>
                    </label>
                    <select
                      className={styles.select}
                      value={form.type}
                      onChange={(e) => setField("type", e.target.value)}
                    >
                      <option value="API">API</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="Offline">Offline</option>
                    </select>
                  </div>

                  {/* Status toggle */}
                  <div className={styles.field}>
                    <label className={styles.label}>Status</label>
                    <div className={styles.statusToggleWrapper}>
                      <input
                        type="checkbox"
                        id="statusToggle"
                        className={styles.statusToggle}
                        checked={form.status === "Active"}
                        onChange={(e) =>
                          setField("status", e.target.checked ? "Active" : "Inactive")
                        }
                      />
                      <label
                        htmlFor="statusToggle"
                        className={styles.statusToggleLabel}
                      >
                        <span className={styles.statusToggleText}>
                          {form.status}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Internal Notes */}
                  <div className={styles.fieldFull}>
                    <label className={styles.label}>Internal Notes</label>
                    <textarea
                      className={styles.textarea}
                      placeholder="Initial integration completed on Jan 15, 2026. Primary contact: Tech Support Team."
                      rows={3}
                      value={form.internalNotes}
                      onChange={(e) => setField("internalNotes", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Audit Information */}
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Calendar size={18} />
                  Audit Information
                </h3>
                <div className={styles.auditGrid}>
                  <div className={styles.auditCard}>
                    <div className={styles.auditLabel}>Created At</div>
                    <div className={styles.auditValue}>
                      {supplier.createdAt ?? "January 15, 2026 at 10:30 AM"}
                    </div>
                    <div className={styles.auditBy}>
                      By: {supplier.createdBy ?? "System Admin"}
                    </div>
                  </div>
                  <div className={styles.auditCard}>
                    <div className={styles.auditLabel}>Updated At</div>
                    <div className={styles.auditValue}>
                      {supplier.updatedAt ?? "February 1, 2026 at 2:45 PM"}
                    </div>
                    <div className={styles.auditBy}>
                      By: {supplier.updatedBy ?? "John Doe"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ════ Services Tab ════ */}
          {activeTab === "services" && (
            <div className={styles.tabContent}>
              <div className={styles.servicesList}>
                {INITIAL_SERVICES.map((service) => {
                  const enabled = form.services[service.key];
                  const isHotel = service.key === "Hotels";
                  const roleOptions = isHotel ? HOTEL_COMMERCIAL_ROLES : OTHER_COMMERCIAL_ROLES;
                  const currentRole = form.serviceRoles[service.key] || "MERCHANT";
                  return (
                    <div
                      key={service.key}
                      className={`${styles.serviceCard} ${
                        enabled ? styles.serviceEnabled : ""
                      }`}
                    >
                      <div className={styles.serviceCardTop}>
                        <div className={styles.serviceIcon}>
                          <Package size={20} />
                        </div>
                        <div className={styles.serviceInfo}>
                          <div className={styles.serviceName}>{service.name}</div>
                          <div className={styles.serviceStatus}>
                            {enabled ? "Enabled" : "Disabled"}
                          </div>
                        </div>
                        <div className={styles.serviceToggle}>
                          <input
                            type="checkbox"
                            id={`service-${service.key}`}
                            className={styles.toggle}
                            checked={enabled}
                            onChange={() => toggleService(service.key)}
                          />
                        </div>
                      </div>
                      {enabled && (
                        <div className={styles.serviceCardRole}>
                          <span className={styles.serviceCardRoleLabel}>Commercial Role</span>
                          <select
                            className={styles.serviceCardRoleSelect}
                            value={currentRole}
                            onChange={(e) => setServiceRole(service.key, e.target.value)}
                          >
                            {roleOptions.map(r => (
                              <option key={r.key} value={r.key}>{r.label}</option>
                            ))}
                          </select>
                          {isHotel && currentRole === "BOTH" && (
                            <div style={{ marginTop: 8 }}>
                              <span className={styles.serviceCardRoleLabel}>Default Role in Booking</span>
                              <select
                                className={styles.serviceCardRoleSelect}
                                value={form.serviceDefaultRoles[service.key] || "MERCHANT"}
                                onChange={(e) => setServiceDefaultRole(service.key, e.target.value)}
                              >
                                <option value="MERCHANT">Merchant (own risk)</option>
                                <option value="AGENT">Agent (intermediary)</option>
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className={styles.infoBox}>
                <AlertCircle size={18} />
                <div>
                  <div className={styles.infoTitle}>Service Scope Rules</div>
                  <ul className={styles.infoList}>
                    <li>At least one service must be enabled</li>
                    <li>Multiple selections are allowed</li>
                    <li>Changes will affect supplier capabilities immediately</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* ════ VAT / Country Tab ════ */}
          {activeTab === "vat" && (
            <div className={styles.tabContent}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  VAT &amp; Country Configuration
                </h3>
                <div className={styles.grid}>

                  <div className={styles.field}>
                    <label className={styles.label}>
                      Country <span className={styles.required}>*</span>
                    </label>
                    <SearchableSelect
                      value={form.country}
                      onChange={(val) => setField("country", val)}
                      options={countries.map(c => ({ value: c.name, label: c.name }))}
                      placeholder="Select country"
                      searchPlaceholder="Search countries..."
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>
                      Supplier VAT Role <span className={styles.required}>*</span>
                    </label>
                    <select
                      className={styles.select}
                      value={form.vatRole}
                      onChange={(e) => setField("vatRole", e.target.value)}
                    >
                      <option>EU travel agency</option>
                      <option>Belgian travel agency</option>
                      <option>Non-EU travel agency</option>
                    </select>
                  </div>

                  <div className={styles.fieldFull}>
                    <div className={styles.euStatusBox}>
                      <div className={styles.euBadge}>EU Member State</div>
                      <span className={styles.euText}>
                        Automatically determined based on selected country
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.warningBox}>
                <AlertCircle size={18} />
                <div>
                  <div className={styles.warningTitle}>
                    VAT Configuration Notes
                  </div>
                  <ul className={styles.infoList}>
                    <li>EU/Non-EU status is derived automatically from country selection</li>
                    <li>No manual EU toggle is available</li>
                    <li>VAT role affects invoice and tax calculations</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* ════ Contact Tab ════ */}
          {activeTab === "contact" && (
            <div className={styles.tabContent}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Contact Information</h3>
                <div className={styles.grid}>

                  <div className={styles.fieldFull}>
                    <label className={styles.label}>
                      Contact Email <span className={styles.required}>*</span>
                    </label>
                    <div className={styles.inputWithIcon}>
                      <Mail size={18} className={styles.inputIcon} />
                      <input
                        type="email"
                        value={form.contactEmail}
                        onChange={(e) => setField("contactEmail", e.target.value)}
                        className={styles.input}
                      />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>Phone</label>
                    <div className={styles.inputWithIcon}>
                      <Phone size={18} className={styles.inputIcon} />
                      <input
                        type="tel"
                        placeholder="+33 1 23 45 67 89"
                        value={form.contactPhone}
                        onChange={(e) => setField("contactPhone", e.target.value)}
                        className={styles.input}
                      />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>Contact Person Name</label>
                    <div className={styles.inputWithIcon}>
                      <User size={18} className={styles.inputIcon} />
                      <input
                        type="text"
                        placeholder="Jean Dupont"
                        value={form.contactPersonName}
                        onChange={(e) =>
                          setField("contactPersonName", e.target.value)
                        }
                        className={styles.input}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.infoBox}>
                <User size={18} />
                <div>
                  <div className={styles.infoTitle}>
                    Contact Information Usage
                  </div>
                  <p className={styles.infoText}>
                    This information is used for operational communications,
                    technical support requests, and business correspondence.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SupplierDetail;